<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import ProgressSpinner from 'primevue/progressspinner';
import { useEndpoint } from '@apparatix/composables';
import { InputGroup, Section, Typography } from '@apparatix/library/core';
import DetailEditorBase from '@apparatix/leads/DetailEditorBase.vue';
import type { InventoryOverview } from './types';

const props = defineProps<{ id: number; overview?: InventoryOverview | null }>();

// When the parent flyout already loaded the overview (for the header), use it and
// skip a second request. Otherwise fetch it so this tab is standalone-capable.
const fetched = ref<InventoryOverview | null>(null);
const isLoading = ref(false);

const data = computed<InventoryOverview | null>(() => props.overview ?? fetched.value);

const { makeRequest, setEndpointUrl } = useEndpoint<{ data: InventoryOverview }>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    fetched.value = response.data;
    isLoading.value = false;
  },
  onError: () => {
    isLoading.value = false;
  },
  toast: { showOnSuccess: false },
});

type Field = { label: string; value: string | number | null };

const leftFields = computed<Field[]>(() => {
  const o = data.value;
  if (!o) {
    return [];
  }

  return [
    { label: 'Inventory Number', value: o.invNumber },
    { label: 'Status', value: o.status },
    { label: 'Product Type', value: o.productType },
    { label: 'Dimensions', value: o.dimensions },
    { label: 'Segments', value: o.segments },
    { label: 'Read', value: o.read },
    { label: 'Direction of Travel', value: o.directionOfTravel },
    { label: 'Illuminated', value: o.illuminated ? (o.illuminatedDuration ?? 'Yes') : 'No' },
  ];
});

const rightFields = computed<Field[]>(() => {
  const o = data.value;
  if (!o) {
    return [];
  }

  return [
    { label: 'Geopath Frame ID', value: o.geopathNumber },
    { label: 'Activation Date', value: o.activationDate },
    { label: 'Retirement Date', value: o.retirementDate },
    { label: 'Description', value: o.description },
    { label: 'Marketing Description', value: o.marketingDescription },
  ];
});

const hasValue = (value: string | number | null): boolean => value !== null && value !== '';

// The flyout reuses this component instance across inventory ids without
// remounting, so refetch on every props.id change rather than only onMounted.
// Skipped when the parent supplies props.overview (already reactive to the id).
const loadOverview = () => {
  if (props.overview) {
    return;
  }

  fetched.value = null;
  isLoading.value = true;
  setEndpointUrl(`/manage-measurements/inventory/${props.id}/overview`);
  makeRequest();
};

watch(() => props.id, loadOverview);
onMounted(loadOverview);
</script>
<template>
  <div class="flex flex-col gap-6">
    <div v-if="isLoading && !data" class="flex justify-center py-10">
      <ProgressSpinner style="width: 2.5rem; height: 2.5rem" />
    </div>
    <Section v-else-if="data" :footer="false">
      <DetailEditorBase left-col-header="Details" right-col-header="Marketing &amp; Dates">
        <template #left-col>
          <InputGroup v-for="field in leftFields" :key="field.label">
            <Typography variant="bold-label">
              {{ field.label }}
            </Typography>
            <Typography v-if="hasValue(field.value)" variant="form-label">
              {{ field.value }}
            </Typography>
            <Typography v-else muted variant="form-label">
              Empty
            </Typography>
          </InputGroup>
        </template>
        <template #right-col>
          <InputGroup v-for="field in rightFields" :key="field.label">
            <Typography variant="bold-label">
              {{ field.label }}
            </Typography>
            <Typography v-if="hasValue(field.value)" variant="form-label">
              {{ field.value }}
            </Typography>
            <Typography v-else muted variant="form-label">
              Empty
            </Typography>
          </InputGroup>
        </template>
      </DetailEditorBase>
    </Section>
  </div>
</template>
